import React from 'react';
import { HiOutlineLockClosed, HiOutlineTrash, HiOutlineVariable } from 'react-icons/hi';
import ToggleSwitch from '../../../../atoms/ToogleSwitch';
import TextEdit from '../../../../molecules/TextEdit';
import SelectTextField from '../../../../molecules/SelectTextField';
import { TemplateVariable, VARIABLE_FORMAT_LABELS, VariableFormat } from '../../../../../types/communication';
import { SystemConstant } from '../../../../../utils/communicationConstants';
import SectionCard from './SectionCard';

const FORMATS = Object.keys(VARIABLE_FORMAT_LABELS) as VariableFormat[];

interface VariablesPanelProps {
    variables: TemplateVariable[];
    unused: string[];
    constants: SystemConstant[];
    disabled: boolean;
    onUpdate: (name: string, patch: Partial<TemplateVariable>) => void;
    onRemove: (name: string) => void;
}

const VariablesPanel: React.FC<VariablesPanelProps> = ({ variables, unused, constants, disabled, onUpdate, onRemove }) => (
    <>
        {constants.length > 0 && (
            <SectionCard
                title="Constantes do sistema"
                description="Preenchidas automaticamente pelo sistema, do ambiente ou do envio. Não aparecem no formulário de quem envia."
                icon={<HiOutlineLockClosed size={18} />}
                tone="muted"
            >
                <ul className="flex flex-col gap-2">
                    {constants.map((c) => (
                        <li key={c.name} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
                            <span className="text-gray-800 dark:text-gray-100">{c.label}</span>
                            <code className="text-[11px] text-violet-600 dark:text-violet-300">{`{{${c.name}}}`}</code>
                            <span className="text-xs text-gray-400">
                                {c.scope === 'ENVIRONMENT' ? 'produção' : 'exemplo'}:{' '}
                                <span className="font-mono">{c.valueByEnvironment.prd}</span>
                            </span>
                        </li>
                    ))}
                </ul>
            </SectionCard>
        )}

        <SectionCard
            title={`${variables.length} variáveis`}
            description="Rótulo e formato de cada campo. Toda variável é preenchida a cada envio; dado fixo se escreve direto no modelo."
            icon={<HiOutlineVariable size={18} />}
        >
            {!variables.length ? (
                <p className="text-sm text-gray-400">Nenhuma variável no conteúdo ainda.</p>
            ) : (
                <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                                <th className="px-2 py-1.5 font-medium">Rótulo e nome</th>
                                <th className="px-2 py-1.5 font-medium w-48">Formato</th>
                                <th className="px-2 py-1.5 font-medium w-24 text-center">Obrigatória</th>
                                <th className="px-2 py-1.5 w-8" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                            {variables.map((v) => {
                                const isUnused = unused.includes(v.name);
                                return (
                                    <tr key={v.name} className={isUnused ? 'opacity-60' : ''}>
                                        <td className="px-2 py-2 align-top">
                                            <TextEdit
                                                id={`var-label-${v.name}`}
                                                label="Rótulo"
                                                value={v.label}
                                                disabled={disabled}
                                                errorMessage={!v.label.trim() ? 'Obrigatório' : undefined}
                                                onChange={(e) => onUpdate(v.name, { label: e.target.value })}
                                            />
                                            <div className="flex items-center gap-2 mt-1">
                                                <code className="text-[11px] text-gray-400">{`{{${v.name}}}`}</code>
                                                {isUnused && (
                                                    <span className="rounded bg-amber-100 text-amber-800 px-1 text-[10px] uppercase tracking-wide">não usada</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-2 py-2 align-top">
                                            <SelectTextField
                                                id={`var-format-${v.name}`}
                                                label="Formato"
                                                value={v.format}
                                                disabled={disabled}
                                                hideEmptyOption
                                                items={FORMATS.map((f) => ({ value: f, label: VARIABLE_FORMAT_LABELS[f] }))}
                                                onChange={(e) => onUpdate(v.name, { format: e.target.value as VariableFormat })}
                                            />
                                        </td>
                                        <td className="px-2 py-2 align-top">
                                            <div className="flex justify-center pt-1">
                                                <ToggleSwitch
                                                    checked={v.required}
                                                    disabled={disabled}
                                                    activeClass="bg-teal-500"
                                                    onChange={(required) => onUpdate(v.name, { required })}
                                                />
                                            </div>
                                        </td>
                                        <td className="px-2 py-2 align-top">
                                            {!disabled && isUnused && (
                                                <button
                                                    type="button"
                                                    onClick={() => onRemove(v.name)}
                                                    className="mt-1 text-gray-400 hover:text-red-600"
                                                    title="Remover variável não usada"
                                                    aria-label="Remover variável"
                                                >
                                                    <HiOutlineTrash size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </SectionCard>
    </>
);

export default VariablesPanel;
